/**
 * Modale di conferma per modifiche/cancellazioni/spostamenti
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Alert,
  Chip,
  Divider
} from '@mui/material';
import {
  Warning,
  DriveFileMove,
  Delete,
  Edit
} from '@mui/icons-material';
import { BookingEvent } from '../../services/calendarService';

interface BookingConfirmModalProps {
  open: boolean;
  changeType: 'move' | 'create' | 'delete';
  oldData?: BookingEvent;
  newData?: Partial<BookingEvent>;
  onConfirm: () => void;
  onCancel: () => void;
}

const BookingConfirmModal: React.FC<BookingConfirmModalProps> = ({
  open,
  changeType,
  oldData,
  newData,
  onConfirm,
  onCancel
}) => {
  const formatDateTime = (date?: Date) => {
    if (!date) return 'N/D';
    return new Date(date).toLocaleString('it-IT', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  };

  const getIcon = () => {
    switch (changeType) {
      case 'move': return <DriveFileMove color="warning" sx={{ fontSize: 40 }} />;
      case 'delete': return <Delete color="error" sx={{ fontSize: 40 }} />;
      case 'create': return <Edit color="primary" sx={{ fontSize: 40 }} />;
    }
  };

  const getTitle = () => {
    switch (changeType) {
      case 'move': return 'Conferma Spostamento';
      case 'delete': return 'Conferma Cancellazione';
      case 'create': return 'Conferma Creazione';
    }
  };

  const getAlertSeverity = () => {
    switch (changeType) {
      case 'move': return 'warning';
      case 'delete': return 'error';
      case 'create': return 'info';
      default: return 'info';
    }
  };

  const getMessage = () => {
    switch (changeType) {
      case 'move':
        return 'Stai per spostare questo appuntamento. Verifica che i nuovi dati siano corretti.';
      case 'delete':
        return 'Stai per CANCELLARE questo appuntamento. Questa operazione NON può essere annullata!';
      case 'create':
        return 'Stai per creare un nuovo appuntamento. Verifica che i dati siano corretti.';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {getIcon()}
          <Typography variant="h6">{getTitle()}</Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity={getAlertSeverity() as any} sx={{ mb: 3 }}>
          {getMessage()}
        </Alert>

        {/* Mostra i dati dell'appuntamento */}
        {changeType === 'delete' && oldData && (
          <Box>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Appuntamento da cancellare:
            </Typography>
            <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Paziente:</strong> {oldData.patientName || 'N/D'}
              </Typography>
              <Typography variant="body2">
                <strong>Prestazione:</strong> {oldData.serviceName || oldData.serviceCode || 'N/D'}
              </Typography>
              <Typography variant="body2">
                <strong>Data/Ora:</strong> {formatDateTime(oldData.start)}
              </Typography>
              {oldData.bookingCode && (
                <Typography variant="body2">
                  <strong>Codice:</strong> {oldData.bookingCode}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* Mostra confronto vecchi/nuovi dati per spostamento */}
        {changeType === 'move' && oldData && newData && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Appuntamento: <strong>{oldData.patientName || 'N/D'}</strong>
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {oldData.serviceName || oldData.serviceCode || 'Prestazione'}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Data/Ora Vecchia */}
            <Grid item xs={6}>
              <Typography variant="caption" color="textSecondary">
                Data/Ora Attuale
              </Typography>
              <Box sx={{
                p: 1.5,
                backgroundColor: '#ffebee',
                borderRadius: 1,
                border: '1px solid #ef9a9a'
              }}>
                <Typography variant="body2" fontWeight="bold">
                  {formatDateTime(oldData.start)}
                </Typography>
              </Box>
            </Grid>

            {/* Data/Ora Nuova */}
            <Grid item xs={6}>
              <Typography variant="caption" color="textSecondary">
                Nuova Data/Ora
              </Typography>
              <Box sx={{
                p: 1.5,
                backgroundColor: '#e8f5e9',
                borderRadius: 1,
                border: '1px solid #81c784'
              }}>
                <Typography variant="body2" fontWeight="bold">
                  {formatDateTime(newData.start)}
                </Typography>
              </Box>
            </Grid>

            {oldData.bookingCode && (
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">
                  Codice Prenotazione: <Chip label={oldData.bookingCode} size="small" />
                </Typography>
              </Grid>
            )}
          </Grid>
        )}

        {/* Mostra solo nuovi dati per creazione */}
        {changeType === 'create' && newData && (
          <Box>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Nuovo appuntamento:
            </Typography>
            <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Data/Ora Inizio:</strong> {formatDateTime(newData.start)}
              </Typography>
              <Typography variant="body2">
                <strong>Data/Ora Fine:</strong> {formatDateTime(newData.end)}
              </Typography>
              <Typography variant="body2">
                <strong>Agenda ID:</strong> {newData.diaryId || 'N/D'}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} color="inherit">
          Annulla
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color={changeType === 'delete' ? 'error' : 'primary'}
          autoFocus
        >
          {changeType === 'delete' ? 'Conferma Cancellazione' : 'Conferma'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BookingConfirmModal;
